import type { AudioEngine } from './AudioEngine';

export class AmbientDrone {
  private engine: AudioEngine;
  private oscs: OscillatorNode[] = [];
  private gains: GainNode[] = [];
  private lfo: OscillatorNode | null = null;
  private running = false;

  constructor(engine: AudioEngine) {
    this.engine = engine;
  }

  start(): void {
    const ctx = this.engine.context;
    const bus = this.engine.ambientBus;
    if (!ctx || !bus || this.running) return;

    this.running = true;
    const now = ctx.currentTime;

    // Layer 1: deep sine pad
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.value = 55; // A1
    gain1.gain.value = 0.15;
    osc1.connect(gain1).connect(bus);
    osc1.start(now);

    // Layer 2: higher triangle pad
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'triangle';
    osc2.frequency.value = 110; // A2
    gain2.gain.value = 0.08;
    osc2.connect(gain2).connect(bus);
    osc2.start(now);

    // Layer 3: subtle fifth for richness
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.type = 'sine';
    osc3.frequency.value = 82.5; // E2 (fifth above A1)
    gain3.gain.value = 0.06;
    osc3.connect(gain3).connect(bus);
    osc3.start(now);

    // LFO for subtle volume modulation
    this.lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    this.lfo.type = 'sine';
    this.lfo.frequency.value = 0.1; // very slow
    lfoGain.gain.value = 0.03;
    this.lfo.connect(lfoGain);
    lfoGain.connect(gain1.gain);
    lfoGain.connect(gain2.gain);
    this.lfo.start(now);

    this.oscs = [osc1, osc2, osc3];
    this.gains = [gain1, gain2, gain3];
  }

  /** Update tension 0-1 (0 = calm, 1 = chaos) */
  setTension(tension: number): void {
    if (!this.running || this.gains.length === 0) return;
    const ctx = this.engine.context;
    if (!ctx) return;
    const now = ctx.currentTime;

    // Increase volume with tension
    const baseVol = 0.08 + tension * 0.15;
    this.gains[0].gain.linearRampToValueAtTime(baseVol, now + 0.5);
    this.gains[1].gain.linearRampToValueAtTime(baseVol * 0.6, now + 0.5);
    this.gains[2].gain.linearRampToValueAtTime(baseVol * 0.4, now + 0.5);

    // Shift pitch slightly with tension (dissonance)
    if (this.oscs.length >= 3) {
      this.oscs[2].frequency.linearRampToValueAtTime(
        82.5 + tension * 10, // detune for tension
        now + 1
      );
    }

    // Speed up LFO with tension
    if (this.lfo) {
      this.lfo.frequency.linearRampToValueAtTime(0.1 + tension * 2, now + 1);
    }
  }

  stop(): void {
    if (!this.running) return;
    const ctx = this.engine.context;
    if (!ctx) return;
    const now = ctx.currentTime;

    // Fade out
    this.gains.forEach(g => {
      g.gain.linearRampToValueAtTime(0, now + 0.5);
    });

    // Stop after fade
    setTimeout(() => {
      this.oscs.forEach(o => { try { o.stop(); } catch {} });
      if (this.lfo) { try { this.lfo.stop(); } catch {} }
      this.oscs = [];
      this.gains = [];
      this.lfo = null;
      this.running = false;
    }, 600);
  }
}
